import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailOutletSetupTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailOutletSetup = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [outletName, setOutletName] = useState<string | null>(null);
  const [terminalId, setTerminalId] = useState<string | null>(null);
  const [profitCenterId, setProfitCenterId] = useState<string | null>(null);
  const [autoPrintReceipts, setAutoPrintReceipts] = useState<boolean>(true);
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [terminalErr, setTerminalErr] = useState<boolean>(false);
  const [profitCenterErr, setProfitCenterErr] = useState<boolean>(false);
  const [retailOutlets, setRetailOutlets] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Outlet Name": outletName,
    "Terminal ID": terminalId,
    "Profit Center ID": profitCenterId ?? "",
    "Auto Print Receipts": autoPrintReceipts ? "Yes" : "No",
    Active: active ? "Yes" : "No",
  };

  const validateInput = () => {
    setNameError(false);
    setTerminalErr(false);
    setProfitCenterErr(false);
    if (!outletName || outletName === "") {
      setAlertMessage("Outlet Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      outletName &&
      !validateString(
        outletName,
        validationLength.outletName,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }
    if (terminalId && terminalId !== "" && !/^\d+$/.test(terminalId)) {
      setTerminalErr(true);
      return false;
    }
    if (
      profitCenterId &&
      profitCenterId !== "" &&
      !/^\d+$/.test(profitCenterId)
    ) {
      setProfitCenterErr(true);
      return false;
    }
    return true;
  };

  const editRetailOutlets = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setOutletName(null);
      setTerminalId(null);
      setProfitCenterId(null);
      setAutoPrintReceipts(true);
      setActive(true);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setOutletName(list?.["Outlet Name"] ?? null);
    setTerminalId(
      list?.["Terminal ID"] != null ? String(list["Terminal ID"]) : null,
    );
    setProfitCenterId(
      list?.["Profit Center ID"] != null && list["Profit Center ID"] !== ""
        ? String(list["Profit Center ID"])
        : null,
    );
    setAutoPrintReceipts(list?.["Auto Print Receipts"] === "Yes");
    setActive(list?.["Active"] === "Yes");
  };

  const updateRetailOutlets = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailOutlets.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.OUTLETNAME,
        outletName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setTerminalErr(false);
    setProfitCenterErr(false);
    setRetailOutlets((prev: any) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailOutlets(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RetailPropertyEnum.outletSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailOutlets = () => {
    if (!validateInput()) return;
    if (checkDuplicate(retailOutlets, DNDFieldsEnum.OUTLETNAME, outletName)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailOutlets((prev: any) =>
      updateData(
        prev !== null && prev?.length
          ? [...prev, { id: retailOutlets.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailOutlets(null);
    setEditClicked(false);
  };

  const deleteRetailOutlets = (id: number) => {
    setRetailOutlets(deleteItem(retailOutlets, id));
  };

  useEffect(() => {
    if (fieldValues !== undefined && fieldValues !== null) {
      setRetailOutlets(fieldValues);
    }
  }, []);

  useEffect(() => {
    data(retailOutlets);
    if (editClicked) {
      setSelectedId(checkData(retailOutlets, selectedId) ? selectedId : null);
    }
  }, [retailOutlets]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Outlet Setup
              <Tooltip
                title={tooltipComponent(retailOutletSetupTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailOutlets(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailOutlets(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Outlet Name
              <Tooltip
                title={tooltipComponent(retailOutletSetupTooltip.outletName)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailOutletName"
              variant="outlined"
              placeholder="Outlet Name"
              value={outletName ?? ""}
              helperText={
                nameError
                  ? validationMessages.outletName
                  : fieldValidationHelperText.outletName
              }
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setOutletName(e.target.value || null);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Terminal ID
              <Tooltip
                title={tooltipComponent(retailOutletSetupTooltip.terminalId)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailTerminalId"
              variant="outlined"
              placeholder="1"
              value={terminalId ?? ""}
              helperText={terminalErr ? "Enter numbers only" : ""}
              error={terminalErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/\D/g, "");
                setTerminalId(v || null);
                setTerminalErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Profit Center ID
              <Tooltip
                title={tooltipComponent(
                  retailOutletSetupTooltip.profitCenterId,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailProfitCenterId"
              variant="outlined"
              placeholder="Profit Center ID"
              value={profitCenterId ?? ""}
              helperText={profitCenterErr ? "Enter numbers only" : ""}
              error={profitCenterErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/\D/g, "");
                setProfitCenterId(v || null);
                setProfitCenterErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Auto Print Receipts?
              <Tooltip
                title={tooltipComponent(
                  retailOutletSetupTooltip.autoPrintReceipts,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={autoPrintReceipts}
                  onChange={() => setAutoPrintReceipts(!autoPrintReceipts)}
                />
              }
              label=""
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Active?
              <Tooltip
                title={tooltipComponent(retailOutletSetupTooltip.active)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateRetailOutlets : addRetailOutlets}
              id="addRetailOutlet"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Outlet
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Outlets Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailOutlets?.length ? (
                <DraggableItemList
                  itemData={retailOutlets}
                  onOrderUpdate={(order) => setRetailOutlets(order)}
                  onEdit={(info) => {
                    editRetailOutlets(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailOutlets(id)}
                  code={DNDFieldsEnum.OUTLETNAME}
                  name={DNDFieldsEnum.TERMINALID}
                />
              ) : (
                <Typography variant="body2">
                  No Outlet Setup Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailOutletSetup;
